import { tryOnScopeDispose, useIntervalFn, useMutationObserver } from "@vueuse/core";
import { ref, watch } from "vue";
import { usePurchaseStore } from "#ce/components/SpCheckout/store/purchase";
import { defineStore } from "#ce/utils/store";

const LINK_MODAL_FRAME = 'body > div > iframe[src*="link-modal"]';

// Scoped to the root custom element, like the purchase store: SpCheckout and its PaymentPanel share
// one state. The Link check scans the whole page, though, so Link's window hides every checkout on it.
const useModalVisibility = defineStore("checkoutModalVisibility", () => ({
  isHiddenTemporarily: ref(false),
  challengeShown: ref(false),
  linkModalShown: ref(false),
}));

/**
 * Hides the checkout modal while one of Stripe's own windows is open on top of it, and shows it
 * again once none is. The checkout modal is a native <dialog> in the browser's top layer, so a
 * window Stripe adds to the page would otherwise sit underneath it, out of reach.
 *
 * Two of Stripe's windows are watched, each tracked on its own so one closing never reveals the
 * checkout over the other:
 * - the card authentication (3DS) challenge, looked for while a payment is being confirmed and
 *   treated as open until the confirmation ends;
 * - the Link window, for example the code Link texts a returning user, for as long as the checkout
 *   is mounted.
 *
 * `isHiddenTemporarily` is shared within one checkout, so PaymentPanel can set it and SpCheckout
 * can read it to hide and show the modal.
 *
 * @returns {Object}
 * @property {Ref<boolean>} isHiddenTemporarily
 */
export function useModalWatcher() {
  const purchaseStore = usePurchaseStore();
  const { isHiddenTemporarily, challengeShown, linkModalShown } = useModalVisibility();
  let linkModalWatcher = null;
  let challengeFrameWatcher = null;

  function startChallengeWatcher() {
    stopChallengeWatcher();

    const watchInterval = 400;
    const maxRetries = 500;

    let retries = 0;
    let watcher;

    const watchHandler = () => {
      if (challengeFrameWatcher !== watcher) {
        return;
      }

      const frames = Array.from(document.getElementsByTagName("iframe"));
      const challengeFrame = frames.find(({ src }) => src.includes("-challenge"));

      if (challengeFrame) {
        challengeShown.value = true;
        syncHidden();
      }

      if (retries >= maxRetries || challengeFrame) {
        stopChallengeWatcher();
      }

      retries++;
    };

    watcher = useIntervalFn(watchHandler, watchInterval);
    challengeFrameWatcher = watcher;
  }

  function stopChallengeWatcher() {
    challengeFrameWatcher?.pause();
    challengeFrameWatcher = null;
  }

  watch(purchaseStore.aboutToConfirmPayment, (value) => {
    if (value) {
      startChallengeWatcher();
    } else {
      stopChallengeWatcher();
      cleanup();
    }
  });

  // Stripe mounts the Link modal hidden on <body> when the payment form loads and shows it by
  // restyling its container, for example to ask a returning Link user for the code it texted them.
  // It opens whatever the payment type, so the watcher runs for as long as the checkout does.
  function startLinkModalWatcher() {
    linkModalWatcher = useMutationObserver(document.body, syncLinkModal, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });
  }

  function syncLinkModal() {
    const open = Array.from(document.querySelectorAll(LINK_MODAL_FRAME)).some(isShown);

    if (open !== linkModalShown.value) {
      linkModalShown.value = open;
      syncHidden();
    }
  }

  function isShown(frame) {
    return frame.parentElement?.style.display !== "none";
  }

  startLinkModalWatcher();

  function syncHidden() {
    isHiddenTemporarily.value = challengeShown.value || linkModalShown.value;
  }

  function cleanup() {
    challengeShown.value = false;
    syncHidden();
  }

  function stop() {
    stopChallengeWatcher();
    linkModalWatcher?.stop();
    cleanup();
  }

  tryOnScopeDispose(() => {
    stop();
  });

  return {
    // State
    isHiddenTemporarily,
  };
}
